import React, { useState, useMemo, useEffect } from 'react';
import { Download, FileText, Filter, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { api, API } from '../auth/AuthContext';

function ResourcesHero() {
  return (
    <section className="coral-gradient text-white relative overflow-hidden">
      <div className="absolute inset-0 dotted-bg opacity-60" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="text-white/90 text-xs font-semibold uppercase tracking-[0.18em]">Study Resources</div>
        <h1 className="font-serif font-semibold text-5xl md:text-6xl mt-4 leading-[1.05] max-w-4xl">
          Sample papers & <span className="gold-text italic">question papers.</span>
        </h1>
        <p className="mt-6 text-white/90 text-lg max-w-2xl">Curated practice materials for ICSE, CBSE & ISC — organised by board, class and subject.</p>
      </div>
    </section>
  );
}

function ResourceFilters({ search, setSearch, board, setBoard, type, setType }) {
  return (
    <div className="bg-white rounded-2xl border border-orange-100 p-5 grid grid-cols-1 md:grid-cols-4 gap-3 mb-8">
      <Input placeholder="Search papers..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="resources-search-input" className="md:col-span-2" />
      <Select value={board} onValueChange={setBoard}>
        <SelectTrigger data-testid="resources-board-filter"><SelectValue placeholder="Board" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Boards</SelectItem>
          <SelectItem value="ICSE">ICSE</SelectItem>
          <SelectItem value="CBSE">CBSE</SelectItem>
          <SelectItem value="ISC">ISC</SelectItem>
        </SelectContent>
      </Select>
      <Select value={type} onValueChange={setType}>
        <SelectTrigger data-testid="resources-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          <SelectItem value="Sample Paper">Sample Paper</SelectItem>
          <SelectItem value="Question Paper">Question Paper</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function ResourceCard({ resource: r }) {
  return (
    <div className="bg-white rounded-2xl border border-orange-100 p-6 smooth card-hover">
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl bg-coral-light text-coral flex items-center justify-center">
          <FileText className="w-5 h-5" />
        </div>
        <span className="text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">{r.year}</span>
      </div>
      <h3 className="font-serif font-semibold text-lg mt-4">{r.title}</h3>
      <div className="flex flex-wrap gap-2 mt-3 text-xs">
        <span className="px-2 py-0.5 rounded-full bg-coral-light text-coral font-medium">{r.board}</span>
        <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{r.classLevel}</span>
        <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{r.subject}</span>
      </div>
      <a href={`${API}/resources/${r.id}/download`} target="_blank" rel="noreferrer" data-testid={`resource-download-${r.id}`} className="mt-5 btn-primary border-0 h-10 text-sm rounded-lg flex items-center justify-center gap-2 text-white font-medium">
        <Download className="w-4 h-4" /> Download
      </a>
    </div>
  );
}

export default function Resources() {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [board, setBoard] = useState('all');
  const [type, setType] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (board !== 'all') params.board = board;
    if (type !== 'all') params.type = type;
    api.get('/resources', { params })
      .then((r) => setItems(r.data))
      .catch(() => toast({ title: 'Could not load resources' }))
      .finally(() => setLoading(false));
    // search filters client-side (see useMemo below); toast is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, type]);

  const filtered = useMemo(() => {
    if (!search) return items;
    return items.filter((r) => r.title.toLowerCase().includes(search.toLowerCase()));
  }, [items, search]);

  return (
    <div className="fade-in">
      <ResourcesHero />
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <ResourceFilters search={search} setSearch={setSearch} board={board} setBoard={setBoard} type={type} setType={setType} />

        {loading ? (
          <div className="text-center py-16 text-stone-500">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
            <p className="mt-3 text-sm">Loading resources...</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((r) => <ResourceCard key={r.id} resource={r} />)}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-stone-500 py-12">
                <Filter className="w-8 h-8 mx-auto opacity-40" />
                <p className="mt-3">No resources available yet. Check back soon.</p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
